import { DialogActions, DialogContent } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import { format } from "date-fns";
import React from "react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import currentLocale from "../../helpers/locales";
import useCalendarHandlers from "../../hooks/useCalendarHandlers";
import { Stateful, useStateful } from "../../hooks/useStateful";
import CardHeaderDefault from "../../pages/MainLayout/components/CardHeader/CardHeaderDefault";
import ButtonDefault, { ButtonVariant } from "../Button/ButtonDefault";
import InputDefault, { InputDefaultProps } from "../Form/Input/InputDefault";
import ReadOnlyField from "../Form/ReadOnlyField";
import { CalendarIcon } from "../Icons/Icons";
import ErrorText from "../Text/ErrorText";
import {
  calendarClasses,
  dateFormatNorsk,
  firstSelectableDate,
  numberOfMonths,
} from "./CalendarConstants";

export type DayPickerDefaultProps = {
  id: string;
  label: string;
  date: Stateful<Date>;
  disabled?: boolean;
  errorText?: string;
} & Omit<InputDefaultProps, "ref" | "crossOrigin" | "error">;

const CalendarPicker: React.ForwardRefRenderFunction<HTMLDivElement, DayPickerDefaultProps> = (
  { id, label, date, disabled = false, readOnly, errorText },
  ref,
) => {
  const { t } = useTranslation();
  const showCalendar = useStateful<boolean>(false);
  const {
    handleOpen,
    handleInputChange,
    handleSelect,
    inputValid,
    inputValue,
    inputRef,
    errorMessage,
  } = useCalendarHandlers(date, showCalendar);

  if (readOnly) {
    return (
      <ReadOnlyField
        icon={<CalendarIcon />}
        label="Dato jaktet"
        value={format(date.value, dateFormatNorsk)}
      />
    );
  }
  return (
    <div ref={ref} id={id} data-testid="calendarPicker">
      {!showCalendar?.value ? (
        <>
          <InputDefault
            id={id + "DateInput"}
            ref={inputRef}
            data-testid="fsCalendarInput"
            label={label}
            rightIcon={
              <button
                type="button"
                title="Åpne kalender"
                onClick={() => !disabled && !readOnly && handleOpen()}
              >
                <CalendarIcon />
              </button>
            }
            onClick={() => !disabled && !readOnly && handleOpen()}
            onBlur={handleInputChange}
            defaultValue={format(date.value, dateFormatNorsk)}
            value={inputValue.value}
            validationErrorMessage={!inputValid.value ? errorMessage.value : undefined}
            inputClasses="w-full bg-white"
            disabled={disabled}
            readOnly={readOnly}
          />
          {errorText && <ErrorText>{errorText}</ErrorText>}
        </>
      ) : (
        <div
          className="flex flex-col h-screen justify-center align-middle"
          data-testid="fsCalendar"
        >
          <Dialog
            fullScreen
            fullWidth
            maxWidth={"lg"}
            open={showCalendar.value}
            onClose={handleOpen}
          >
            <CardHeaderDefault
              pathProps={{
                pageTitle: t("components.dayPicker.title"),
                leftAction: t("actions.cancel"),
                onLeftActionClick: handleOpen,
              }}
            />

            <DialogContent className="mb-auto overflow-y-auto border-0 text-ldir-brown-900">
              <DayPicker
                id={id + "DayPicker"}
                data-testid="fsDayPicker"
                mode="single"
                ISOWeek={true}
                defaultMonth={firstSelectableDate}
                disabled={{ after: new Date() }}
                endMonth={new Date()}
                disableNavigation={true}
                hideNavigation={true}
                locale={currentLocale()}
                selected={date.value}
                onSelect={handleSelect}
                numberOfMonths={numberOfMonths}
                showOutsideDays
                className="border-0 overflow-auto flex justify-center"
                classNames={calendarClasses}
                reverseMonths
                autoFocus
              />
            </DialogContent>
            <DialogActions className="sticky bg-ldir-blue-900 px-4 py-5">
              <div className="w-full flex justify-end gap-2">
                <ButtonDefault
                  id="calendarGoToToDay"
                  label={t("components.dayPicker.gaTilIdag")}
                  variant={ButtonVariant.TertiaryDarkBg}
                  onClick={() => {
                    date.set(new Date());
                    handleOpen();
                  }}
                />
                <ButtonDefault
                  id="calendarPickerElgLukk"
                  label={t("actions.close")}
                  variant={ButtonVariant.PrimaryDarkBg}
                  onClick={handleOpen}
                />
              </div>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </div>
  );
};

export default React.forwardRef(CalendarPicker);
